import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useProductFacets } from '../../hooks/useProductFacets';
import { getProductThumb } from '../../data/productImages';
import {
  Search, Paperclip,
  ChevronDown, ChevronLeft, ChevronRight, Loader2, Package, ChevronRight as ArrowR,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { Product } from '../../types';
import { productService } from '../../services/productService';
import api from '../../services/api';

// Marche e categorie arrivano da `/api/products/facets` (in cascata sulla
// marca), non più da liste scritte a mano: quella dei brand si fermava a 13
// voci e ignorava tutto il resto del catalogo, e le categorie di
// `PRODUCT_CATEGORIES` usavano gli slug legacy "laser-inkjet"/"opc-drum"/
// "ricambi" che il backend non risolve più — selezionarli mostrava un filtro
// attivo che non filtrava niente.
const ALL_TYPES = [
  { value: '', label: 'Tutti i tipi' },
  { value: 'Originale', label: 'Originale' },
  { value: 'Compatibile', label: 'Compatibile' },
  { value: 'Kit', label: 'Kit' },
];
const PAGE_SIZES = [25, 50, 100];

function FilterSelect({ value, onChange, testId, children }: {
  value: string; onChange: (v: string) => void; testId?: string; children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="appearance-none h-9 pl-3 pr-8 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/20 focus:border-[hsl(214,58%,41%)] cursor-pointer transition-colors w-full sm:min-w-[150px]"
        data-testid={testId}
      >
        {children}
      </select>
      <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
    </div>
  );
}

export default function AdminProductsPage() {
  // Filters
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [productType, setProductType] = useState('');
  // Filtri tri-state: '' = ignora, 'yes' = true, 'no' = false
  const [kitFilter, setKitFilter] = useState<'' | 'yes' | 'no'>('');
  const [boxFilter, setBoxFilter] = useState<'' | 'yes' | 'no'>('');
  const [priceFilter, setPriceFilter] = useState<'' | 'yes' | 'no'>('');
  const [stockFilter, setStockFilter] = useState<'' | 'yes' | 'no'>('');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  // Scelta la marca, le categorie si riducono a quelle davvero presenti sotto
  // di essa; se quella attiva sparisce viene azzerata. `publicOnly: false`
  // perché qui si amministrano anche i prodotti non pubblicati.
  const { facets } = useProductFacets(brand, category, {
    onTipologiaNonPiuValida: () => setCategory(''),
    publicOnly: false,
  });

  // API state
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const triToBool = (v: '' | 'yes' | 'no'): boolean | undefined =>
    v === 'yes' ? true : v === 'no' ? false : undefined;

  const fetchProducts = useCallback(() => {
    setLoading(true);
    productService.list({
      q: search.trim() || undefined,
      brand: brand || undefined,
      category: category || undefined,
      type: productType || undefined,
      isKit: triToBool(kitFilter),
      hasBoxDiscount: triToBool(boxFilter),
      hasPrice: triToBool(priceFilter),
      hasStock: triToBool(stockFilter),
      page,
      limit: pageSize,
    })
      .then(res => { setProducts(res.items); setTotal(res.total); setPages(res.pages); })
      .catch(() => { setProducts([]); setTotal(0); setPages(1); toast.error('Errore nel caricamento prodotti'); })
      .finally(() => setLoading(false));
  }, [search, brand, category, productType, kitFilter, boxFilter, priceFilter, stockFilter, page, pageSize]);

  useEffect(() => {
    const t = setTimeout(fetchProducts, 250);
    return () => clearTimeout(t);
  }, [fetchProducts]);

  useEffect(() => { setPage(1); }, [search, brand, category, productType, kitFilter, boxFilter, priceFilter, stockFilter, pageSize]);

  const pageButtons = useMemo(() => {
    const max = 7;
    if (pages <= max) return Array.from({ length: pages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(page - 3, pages - max + 1));
    return Array.from({ length: max }, (_, i) => start + i);
  }, [page, pages]);

  const handleAzzera = () => {
    setSearch(''); setBrand(''); setCategory(''); setProductType('');
    setKitFilter(''); setBoxFilter(''); setPriceFilter(''); setStockFilter('');
  };

  const hasActiveFilters = !!(search || brand || category || productType || kitFilter || boxFilter || priceFilter || stockFilter);

  const [exporting, setExporting] = useState<'filtered' | 'all' | null>(null);

  const handleExport = async (all: boolean) => {
    setExporting(all ? 'all' : 'filtered');
    try {
      const params: Record<string, string> = {};
      if (!all) {
        if (search.trim()) params.q = search.trim();
        if (brand) params.brand = brand;
        if (category) params.category = category;
        if (productType) params.type = productType;
        if (kitFilter === 'yes') params.isKit = 'true';
        else if (kitFilter === 'no') params.isKit = 'false';
        if (boxFilter === 'yes') params.hasBoxDiscount = 'true';
        else if (boxFilter === 'no') params.hasBoxDiscount = 'false';
        if (priceFilter === 'yes') params.hasPrice = 'true';
        else if (priceFilter === 'no') params.hasPrice = 'false';
        if (stockFilter === 'yes') params.hasStock = 'true';
        else if (stockFilter === 'no') params.hasStock = 'false';
      }
      const qs = new URLSearchParams(params).toString();
      const url = `/api/products/admin/export-csv${qs ? '?' + qs : ''}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = all ? 'prodotti_tutti.csv' : 'prodotti_filtrati.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
      toast.success(all ? 'Export tutti i prodotti avviato' : 'Export prodotti filtrati avviato');
    } catch {
      toast.error('Errore durante l\'export CSV');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="p-4 sm:p-6" data-testid="admin-products-page">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4 sm:mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Gestione Prodotti</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            <strong className="text-foreground tabular-nums">{total.toLocaleString('it-IT')}</strong> prodotti dal catalogo ERP
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Export buttons */}
          <button
            onClick={() => handleExport(false)}
            disabled={exporting !== null}
            className="h-9 px-3 rounded-lg border text-sm font-medium flex items-center gap-1.5 hover:bg-secondary/60 transition-colors disabled:opacity-50"
            title={`Esporta ${hasActiveFilters ? total.toLocaleString('it-IT') + ' prodotti filtrati' : 'tutti i prodotti'} in CSV`}
            data-testid="admin-products-export-filtered-btn"
          >
            {exporting === 'filtered' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">Esporta filtrati</span>
            <span className="sm:hidden">Filtrati</span>
          </button>
          <button
            onClick={() => handleExport(true)}
            disabled={exporting !== null}
            className="h-9 px-3 rounded-lg border text-sm font-medium flex items-center gap-1.5 bg-[hsl(214,58%,41%)] text-white hover:bg-[hsl(214,58%,36%)] border-transparent transition-colors disabled:opacity-50"
            title="Esporta tutti i prodotti in CSV (nessun filtro)"
            data-testid="admin-products-export-all-btn"
          >
            {exporting === 'all' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">Esporta tutti</span>
            <span className="sm:hidden">Tutti</span>
          </button>
          {/* Page size */}
          <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
            <span className="hidden sm:inline">Mostra</span>
            <FilterSelect value={String(pageSize)} onChange={v => setPageSize(Number(v))} testId="admin-products-page-size">
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s} per pag.</option>)}
            </FilterSelect>
          </div>
        </div>
      </div>

      {/* ── Filter bar ─────────────────────────────────────────── */}
      <div className="rounded-xl border bg-white p-3 sm:p-4 mb-4 sm:mb-5 shadow-sm space-y-2 sm:space-y-3">
        {/* Riga 1: ricerca + filtri principali */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr,auto,auto,auto] gap-2 sm:gap-3 items-end">
          <div className="flex items-center gap-2 h-9 px-3 rounded-lg border bg-white focus-within:ring-2 focus-within:ring-[hsl(214,58%,41%)]/20 focus-within:border-[hsl(214,58%,41%)] transition-colors">
            <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cerca SKU, nome, marca…"
              className="flex-1 h-full text-sm outline-none bg-transparent placeholder:text-muted-foreground min-w-0"
              data-testid="admin-products-search-input"
            />
          </div>

          <FilterSelect value={brand} onChange={setBrand} testId="admin-products-filter-brand">
            <option value="">Tutte le marche</option>
            {facets.brands.map(b => <option key={b} value={b}>{b}</option>)}
          </FilterSelect>

          <FilterSelect value={category} onChange={setCategory} testId="admin-products-filter-category">
            <option value="">Tutte le categorie</option>
            {facets.categories.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </FilterSelect>

          <FilterSelect value={productType} onChange={setProductType} testId="admin-products-filter-type">
            {ALL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </FilterSelect>
        </div>

        {/* Riga 2: filtri secondari + azzera */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-[auto,auto,auto,auto,1fr,auto] gap-2 sm:gap-3 items-end">
          <FilterSelect value={kitFilter} onChange={(v) => setKitFilter(v as '' | 'yes' | 'no')} testId="admin-products-filter-kit">
            <option value="">Tutti — Kit</option>
            <option value="yes">Solo Kit</option>
            <option value="no">Esclusi Kit</option>
          </FilterSelect>

          <FilterSelect value={boxFilter} onChange={(v) => setBoxFilter(v as '' | 'yes' | 'no')} testId="admin-products-filter-box">
            <option value="">Tutti — Scatola</option>
            <option value="yes">Con sconto scatola</option>
            <option value="no">Senza sconto scatola</option>
          </FilterSelect>

          <FilterSelect value={priceFilter} onChange={(v) => setPriceFilter(v as '' | 'yes' | 'no')} testId="admin-products-filter-price">
            <option value="">Tutti — Prezzo</option>
            <option value="yes">Con prezzo</option>
            <option value="no">Senza prezzo</option>
          </FilterSelect>

          <FilterSelect value={stockFilter} onChange={(v) => setStockFilter(v as '' | 'yes' | 'no')} testId="admin-products-filter-stock">
            <option value="">Tutti — Giacenza</option>
            <option value="yes">Con giacenza</option>
            <option value="no">Senza giacenza</option>
          </FilterSelect>

          <div className="hidden lg:block" />

          {hasActiveFilters ? (
            <button
              onClick={handleAzzera}
              className="h-9 px-4 rounded-lg border text-sm font-medium text-muted-foreground hover:bg-secondary/60 transition-colors col-span-2 sm:col-span-1"
              data-testid="admin-products-filter-azzera"
            >
              Azzera filtri
            </button>
          ) : null}
        </div>
      </div>

      {/* ── Loading / empty states ─────────────────────────────── */}
      {loading && products.length === 0 ? (
        <div className="rounded-xl border bg-white p-12 text-center shadow-sm">
          <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-[hsl(214,58%,41%)]" />
          <p className="text-xs text-muted-foreground">Caricamento prodotti…</p>
        </div>
      ) : products.length === 0 ? (
        <div className="rounded-xl border bg-white p-12 text-center shadow-sm">
          <Package className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm font-medium">Nessun prodotto trovato</p>
          <p className="text-xs text-muted-foreground mt-1">Prova a modificare i filtri di ricerca.</p>
        </div>
      ) : (
        <>
          {/* ── Mobile cards (sm:hidden) ─────────────────────── */}
          <div className="space-y-2 sm:hidden" data-testid="admin-products-mobile-list">
            {products.map(p => {
              const pAtts = p.attachments || [];
              const hasPrice = p.listino10 != null && p.listino10 > 0;
              const flagNoPrice = p.webVisible === true && !hasPrice;
              return (
                <Link
                  key={p.sku}
                  to={`/admin/prodotti/${encodeURIComponent(p.sku)}`}
                  className="flex items-center gap-3 p-3 rounded-xl border bg-white shadow-sm active:bg-secondary/40 transition-colors"
                  data-testid="admin-products-mobile-row"
                >
                  <img
                    src={getProductThumb(p.sku, p.category)}
                    alt={p.sku}
                    className="w-10 h-10 rounded border object-cover flex-shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] font-mono font-semibold text-[hsl(214,58%,41%)]">{p.sku}</span>
                      {flagNoPrice && (
                        <span className="text-[8px] font-bold uppercase bg-red-100 text-red-700 border border-red-200 px-1 py-0.5 rounded">
                          ⚠ no €
                        </span>
                      )}
                      {p.boxQty && p.boxPrice ? (
                        <span
                          className="text-[8px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-200 px-1 py-0.5 rounded inline-flex items-center gap-0.5"
                          data-testid={`admin-list-box-badge-${p.sku}`}
                          title={`Scatola ${p.boxQty} pz a € ${p.boxPrice.toFixed(2)}`}
                        >
                          📦 Box
                        </span>
                      ) : null}
                      {p.isKit ? (
                        <span
                          className="text-[8px] font-bold uppercase bg-purple-100 text-purple-700 border border-purple-200 px-1 py-0.5 rounded"
                          data-testid={`admin-list-kit-badge-${p.sku}`}
                          title={`Articolo Kit${p.tipoKit ? ' (tipo ' + p.tipoKit + ')' : ''} — prezzo/giacenza derivati dai componenti`}
                        >
                          Kit
                        </span>
                      ) : null}
                      {p.seedOnly ? (
                        <span
                          className="text-[8px] font-bold uppercase bg-orange-100 text-orange-700 border border-orange-200 px-1 py-0.5 rounded"
                          data-testid={`admin-list-demo-badge-${p.sku}`}
                          title="Prodotto demo — non ancora presente nel gestionale NTS"
                        >
                          DEMO
                        </span>
                      ) : null}
                    </div>
                    <div className="text-sm font-medium truncate">{p.title || '—'}</div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span className="text-[10px] text-muted-foreground">{p.brand || '—'}</span>
                      {hasPrice && (
                        <span className="text-[11px] font-bold text-[hsl(214,58%,41%)] tabular-nums">
                          € {p.listino10!.toFixed(2).replace('.', ',')}
                        </span>
                      )}
                      {p.type && p.type !== 'Compatibile' && (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full border bg-blue-50 text-[hsl(214,58%,41%)] border-blue-200">
                          {p.type}
                        </span>
                      )}
                      {pAtts.length > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-[hsl(214,58%,46%)]">
                          <Paperclip className="w-2.5 h-2.5" />{pAtts.length}
                        </span>
                      )}
                    </div>
                  </div>
                  <ArrowR className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </Link>
              );
            })}
          </div>

          {/* ── Desktop table (hidden sm:block) ─────────────── */}
          <div className="hidden sm:block rounded-xl border bg-white overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-[hsl(var(--dtk-canvas))]">
                  <th className="text-left px-3 py-3 text-[10px] font-semibold text-muted-foreground uppercase w-12" />
                  <th className="text-left px-3 py-3 text-[10px] font-semibold text-muted-foreground uppercase w-28">SKU</th>
                  <th className="text-left px-3 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Prodotto</th>
                  <th className="text-right px-3 py-3 text-[10px] font-semibold text-muted-foreground uppercase w-28 hidden md:table-cell">Prezzo</th>
                  <th className="text-left px-3 py-3 text-[10px] font-semibold text-muted-foreground uppercase w-24 hidden lg:table-cell">Tipo</th>
                  <th className="text-left px-3 py-3 text-[10px] font-semibold text-muted-foreground uppercase hidden xl:table-cell">Allegati</th>
                  <th className="text-right px-3 py-3 text-[10px] font-semibold text-muted-foreground uppercase w-20" />
                </tr>
              </thead>
              <tbody data-testid="admin-products-table-body">
                {products.map(p => {
                  const pAtts = p.attachments || [];
                  const hasPrice = p.listino10 != null && p.listino10 > 0;
                  // Visibile sul web ma manca il prezzo: il portale pubblico
                  // lo nasconderà per evitare ordini gratuiti — segnaliamo
                  // questa anomalia all'admin con un badge rosso.
                  const flagNoPrice = p.webVisible === true && !hasPrice;
                  return (
                    <tr
                      key={p.sku}
                      className="border-b transition-colors hover:bg-secondary/40"
                      data-testid="admin-products-row"
                    >
                      <td className="px-3 py-2">
                        <img
                          src={getProductThumb(p.sku, p.category)}
                          alt={p.sku}
                          className="w-8 h-8 rounded border object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs font-medium text-[hsl(214,58%,41%)] whitespace-nowrap">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Link
                            to={`/admin/prodotti/${encodeURIComponent(p.sku)}`}
                            className="hover:underline"
                            data-testid={`admin-products-sku-link-${p.sku}`}
                          >
                            {p.sku}
                          </Link>
                          {flagNoPrice && (
                            <span
                              className="inline-flex items-center text-[8px] font-bold uppercase bg-red-100 text-red-700 border border-red-200 px-1 py-0.5 rounded"
                              title="Prodotto visibile sul web ma senza prezzo: nascosto al pubblico"
                              data-testid="admin-products-no-price-flag"
                            >
                              ⚠ no €
                            </span>
                          )}
                          {p.boxQty && p.boxPrice ? (
                            <span
                              className="inline-flex items-center text-[8px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-200 px-1 py-0.5 rounded"
                              title={`Sconto scatola: ${p.boxQty} pz a € ${p.boxPrice.toFixed(2)}`}
                              data-testid={`admin-products-box-flag-${p.sku}`}
                            >
                              📦 Box
                            </span>
                          ) : null}
                          {p.isKit ? (
                            <span
                              className="inline-flex items-center text-[8px] font-bold uppercase bg-purple-100 text-purple-700 border border-purple-200 px-1 py-0.5 rounded"
                              title={`Articolo Kit${p.tipoKit ? ' (tipo ' + p.tipoKit + ')' : ''} — prezzo/giacenza derivati dai componenti`}
                              data-testid={`admin-products-kit-flag-${p.sku}`}
                            >
                              Kit
                            </span>
                          ) : null}
                          {p.seedOnly ? (
                            <span
                              className="inline-flex items-center text-[8px] font-bold uppercase bg-orange-100 text-orange-700 border border-orange-200 px-1 py-0.5 rounded"
                              title="Prodotto demo — non ancora presente nel gestionale NTS"
                              data-testid={`admin-products-seedonly-flag-${p.sku}`}
                            >
                              DEMO
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm max-w-md">
                        <Link
                          to={`/admin/prodotti/${encodeURIComponent(p.sku)}`}
                          className="truncate font-medium hover:underline hover:text-[hsl(214,58%,41%)] transition-colors block"
                          title={p.title}
                          data-testid={`admin-products-title-link-${p.sku}`}
                        >
                          {p.title || '—'}
                        </Link>
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                          <span>{p.brand || '—'}</span>
                          {/* Su md- mostra il prezzo inline per non perderlo a sm */}
                          {hasPrice && (
                            <span className="md:hidden font-semibold text-[hsl(214,58%,41%)] tabular-nums">
                              · € {p.listino10!.toFixed(2).replace('.', ',')}
                            </span>
                          )}
                          {/* Su lg- mostra anche allegati inline per compattezza */}
                          {pAtts.length > 0 && (
                            <span className="xl:hidden inline-flex items-center gap-0.5 text-[9px] font-semibold text-[hsl(214,58%,46%)]">
                              · <Paperclip className="w-2.5 h-2.5" />{pAtts.length}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right hidden md:table-cell whitespace-nowrap">
                        {hasPrice ? (
                          <span className="text-sm font-bold text-[hsl(214,58%,41%)] tabular-nums" data-testid="admin-products-price">
                            € {p.listino10!.toFixed(2).replace('.', ',')}
                          </span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic">n/d</span>
                        )}
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell">
                        {p.type && p.type !== 'Compatibile' ? (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${
                            p.type === 'Originale'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : p.type === 'Kit'
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-blue-50 text-[hsl(214,58%,41%)] border-blue-200'
                          }`}>
                            {p.type}
                          </span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 hidden xl:table-cell">
                        <div className="flex gap-1 flex-wrap items-center">
                          {pAtts.length > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-[hsl(214,58%,46%)] bg-blue-50 px-1.5 py-0.5 rounded-full border border-blue-200">
                              <Paperclip className="w-2.5 h-2.5" />{pAtts.length}
                            </span>
                          )}
                          {pAtts.length === 0 && (
                            <span className="text-[10px] text-muted-foreground">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Link
                          to={`/admin/prodotti/${encodeURIComponent(p.sku)}`}
                          className="inline-flex items-center gap-1 text-[11px] text-[hsl(214,58%,46%)] hover:underline font-medium"
                          data-testid="admin-products-detail-link"
                        >
                          Dettagli <ArrowR className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ─────────────────────────────────── */}
          {pages > 1 && (
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-3 mt-4 sm:mt-5 text-xs sm:text-sm text-muted-foreground" data-testid="admin-products-pagination">
              <span className="order-2 sm:order-1">
                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} di {total.toLocaleString('it-IT')}
              </span>
              <div className="flex items-center gap-1 order-1 sm:order-2 flex-wrap justify-center">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="w-8 h-8 rounded-lg border flex items-center justify-center disabled:opacity-40 hover:bg-secondary/60 transition-colors"
                  data-testid="admin-products-page-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {pageButtons[0] > 1 && (
                  <>
                    <button
                      onClick={() => setPage(1)}
                      className="w-8 h-8 rounded-lg border text-sm font-medium bg-white hover:bg-secondary/60 transition-colors"
                    >1</button>
                    {pageButtons[0] > 2 && <span className="px-1 text-muted-foreground">…</span>}
                  </>
                )}
                {pageButtons.map(p => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg border text-sm font-medium transition-colors ${
                      page === p
                        ? 'bg-[hsl(214,58%,41%)] text-white border-[hsl(214,58%,41%)]'
                        : 'bg-white hover:bg-secondary/60'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                {pageButtons[pageButtons.length - 1] < pages && (
                  <>
                    {pageButtons[pageButtons.length - 1] < pages - 1 && <span className="px-1 text-muted-foreground">…</span>}
                    <button
                      onClick={() => setPage(pages)}
                      className="w-8 h-8 rounded-lg border text-sm font-medium bg-white hover:bg-secondary/60 transition-colors"
                    >{pages}</button>
                  </>
                )}
                <button
                  disabled={page === pages}
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                  className="w-8 h-8 rounded-lg border flex items-center justify-center disabled:opacity-40 hover:bg-secondary/60 transition-colors"
                  data-testid="admin-products-page-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
